<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useForm, useToast } from 'vuestic-ui'
import { useI18n } from 'vue-i18n'
import { newContact } from '../../../core/requests/Contacts'
import NewCompany from './NewCompany.vue'
import { getAllCompanies } from '../../../core/requests/Company'
import { validators } from '../../../services/utils'
import { onlyNumbersNoDigits } from '../../../core/helpers'

const emit = defineEmits(['close'])
const vOnlyNumbers = onlyNumbersNoDigits

const showNewCompany = ref(false)
const { t } = useI18n()
const isLoading = ref(false)
const companies: any = ref([])
const { isValid, validate, reset } = useForm('formRef')
const form: any = reactive({})

onMounted(() => {
  watch(
    () => form.havecompany,
    async (val) => {
      if (val) await getCompanies()
    },
  )
})
const selectedCompany = computed(() => companies.value.find((f: any) => f.id === form.company))

const { notify } = useToast()
const doProcess = async () => {
  if (validate()) {
    isLoading.value = true
    try {
      const documentType = form?.document_type?.code
      await newContact({
        ...form,
        document_type: documentType,
      })

      notify({
        message: 'Cliente registrado correctamente',
        color: 'success',
      })

      reset()

      emit('close')
    } catch (e: any) {
      console.log(e)
    }

    isLoading.value = false
  }
}

const documentTypes = [
  { code: 1, text: 'DNI' },
  { code: 2, text: 'C.E' },
  { code: 3, text: 'PASAPORTE' },
]

const openSunatRuc = () => {
  window.open(
    'https://e-consultaruc.sunat.gob.pe/cl-ti-itmrconsruc/FrameCriterioBusquedaWeb.jsp',
    '_blank',
    'menubar=1,resizable=0,width=600,height=500',
  )
}
const onClosed = () => {
  showNewCompany.value = false
  getCompanies()
}
const getCompanies = async () => {
  const cData = await getAllCompanies()
  companies.value = cData
}
</script>

<template>
  <div class="bg-blue-50">
    <div>
      <div class="flex justify-between align-center justify-center">
        <h1 class="h5 font-bold m-0">{{ t('contacts.newContact') }}</h1>
        <div class="flex gap-2">
          <VaButton icon="save" color="primary" :disabled="!isValid" :loading="isLoading" @click="doProcess">
            <span class="text-xs px-1">{{ t('contacts.createContact') }}</span>
          </VaButton>
        </div>
      </div>
    </div>

    <div class="py-4 new-customer gap-8">
      <VaForm ref="formRef" immediate class="gap-2">
        <div class="w-full bg-white p-2">
          <!-- <p class="text-sm font-semibold mb-3">Datos personales</p> -->
          <div class="gap-2">
            <div class="columns-1 sm:columns-4 gap-4">
              <div>
                <VaInput
                  v-model="form.fullname"
                  stateful
                  name="fullname"
                  clearable
                  maxlength="50"
                  label="Nombres completos"
                  :rules="[validators.required]"
                />
              </div>
              <div>
                <VaSelect
                  v-model="form.document_type"
                  name="document_type"
                  class="text-xs"
                  :options="documentTypes"
                  no-options-text="-- No hay items --"
                  label="Tipo de documento"
                />
              </div>
              <div>
                <VaInput
                  v-model="form.document"
                  v-only-numbers
                  name="document"
                  stateful
                  clearable
                  maxlength="22"
                  label="N° Documento"
                />
              </div>
              <div>
                <VaInput v-model="form.ruc" v-only-numbers maxlength="22" name="ruc" stateful clearable label="RUC">
                  <template #appendInner>
                    <VaIcon name="open_in_new" class="cursor-pointer" @click="openSunatRuc()" />
                  </template>
                </VaInput>
              </div>
            </div>

            <div class="w-full mt-3">
              <div class="w-full">
                <VaInput v-model="form.address" name="address" stateful clearable label="Dirección de contacto" />
              </div>
            </div>
            <div class="w-full columns-1 sm:columns-4 gap-4 mt-3 pb-2">
              <div class="">
                <VaInput v-model="form.email" name="email" stateful clearable label="Correo electrónico" maxlength="25">
                  <template #prependInner>
                    <VaIcon name="alternate_email" class="material-symbols-outlined -mx-1.5" color="secondary" />
                  </template>
                </VaInput>
              </div>
              <div class="">
                <VaInput
                  v-model="form.email_corporative"
                  name="email_corporative"
                  stateful
                  clearable
                  maxlength="25"
                  label="Correo corporativo"
                >
                  <template #prependInner>
                    <VaIcon name="alternate_email" class="material-symbols-outlined -mx-1.5" color="secondary" />
                  </template>
                </VaInput>
              </div>
              <div class="">
                <VaInput
                  v-model="form.cellphone"
                  v-only-numbers
                  name="cellphone"
                  stateful
                  clearable
                  label="Teléfono"
                  maxlength="12"
                  :rules="[validators.required]"
                >
                  <template #prependInner>
                    <VaIcon name="call" class="material-symbols-outlined -mx-1.5" color="secondary" />
                  </template>
                </VaInput>
              </div>
              <div class="">
                <VaInput
                  v-model="form.whatsapp"
                  v-only-numbers
                  name="whatsapp"
                  maxlength="12"
                  stateful
                  clearable
                  label="WhatsApp"
                >
                  <template #prependInner>
                    <VaIcon name="call" class="material-symbols-outlined -mx-1.5" color="secondary" />
                  </template>
                </VaInput>
              </div>
            </div>

            <div class="w-full mt-5">
              <VaSwitch
                v-model="form.havecompany"
                class="text-xs"
                name="havecompany"
                label="Representa una empresa"
                size="small"
              />
            </div>

            <div v-if="form.havecompany" class="columns-2 sm:columns-3 mt-5">
              <div>
                <VaSelect
                  v-model="form.company"
                  name="company_id"
                  class="text-xs"
                  :options="companies"
                  track-by="id"
                  value-by="id"
                  no-options-text="-- No hay items --"
                  text-by="socialreason"
                  searchable
                  clearable
                  search-placeholder-text="Escribe el RUC"
                  :rules="[validators.required]"
                  label="Selecciona la empresa"
                />
              </div>

              <div class="pt-5">
                <VaButton icon="add" color="primary" :loading="isLoading" @click="showNewCompany = true">
                  <span class="text-xs px-1">{{ t('contacts.createCompany') }}</span>
                </VaButton>
              </div>
            </div>

            <div v-if="selectedCompany" class="bg-blue-50 px-3 py-2 mt-5">
              <p class="font-bold text-sm mb-3">{{ selectedCompany.socialreason }}</p>
              <div class="columns-1 sm:columns-3">
                <div class="mb-2">
                  <VaInput
                    :model-value="selectedCompany.ruc"
                    background="#eee"
                    :placeholder="'Sin información'"
                    name="whatsapp"
                    readonly
                    class="opacity-85 cursor-not-allowed"
                    label="RUC"
                  />
                </div>
                <div class="mb-2">
                  <VaInput
                    :model-value="selectedCompany.phone"
                    background="#eee"
                    :placeholder="'Sin información'"
                    name="whatsapp"
                    readonly
                    class="opacity-85 cursor-not-allowed"
                    label="Teléfono"
                  />
                </div>
                <div class="mb-2">
                  <VaInput
                    :model-value="selectedCompany.whatsapp"
                    :placeholder="'Sin información'"
                    background="#eee"
                    name="whatsapp"
                    readonly
                    class="opacity-85 cursor-not-allowed"
                    label="WhatsApp"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </VaForm>
    </div>

    <div class="pb-3">
      <div class="flex justify-end">
        <div class="flex gap-2">
          <VaButton icon="save" color="primary" :disabled="!isValid" :loading="isLoading" @click="doProcess">
            <span class="text-xs px-1">{{ t('contacts.createContact') }}</span>
          </VaButton>
        </div>
      </div>
    </div>
  </div>

  <VaModal v-model="showNewCompany" hide-default-actions size="340px" close-button blur no-dismiss>
    <div>
      <NewCompany @close="onClosed" />
    </div>
  </VaModal>
</template>

<style lang="scss">
.new-customer {
  &-tabs {
    .va-tabs__content {
      width: 100%;
    }
  }

  .float-profile {
    position: absolute;
    top: 0;
    right: 0;
  }
}
</style>
